import React, { useEffect, useState, useMemo } from "react";
import { useGetCentres } from "../api/settings";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import { Icon } from "@iconify/react";
import TypeIcon from "./Reusables/HubSpokeIcon/TypeIcon";
import { InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

// Styled Control Box
const ControlBox = ({
  selectedLabels,
  placeholder,
  onClick,
  disabled,
  onDelete,
}) => {
  const visibleChips = selectedLabels.slice(0, 4);
  const remainingCount = selectedLabels.length - 4;

  return (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        width: "100%",
        minHeight: "48px",
        border: "1px solid var(--app-border)",
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 0.5,
        px: 2,
        py: 0.5,
        cursor: disabled ? "not-allowed" : "pointer",
        backgroundColor: disabled
          ? "var(--app-surface-2)"
          : "var(--app-surface)",
        justifyContent: "space-between",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.5,
          flexGrow: 1,
        }}
      >
        {selectedLabels.length > 0 ? (
          selectedLabels.length === 1 && selectedLabels[0] === "All Centers" ? (
            <Typography
              sx={{
                color: "var(--app-text)",
                fontSize: "15px",
                fontWeight: 500,
              }}
            >
              All Centers
            </Typography>
          ) : (
            <>
              {visibleChips.map((label) => (
                <Chip
                  key={label}
                  label={label}
                  size="small"
                  onDelete={(e) => {
                    e.stopPropagation();
                    onDelete(label);
                  }}
                  sx={{
                    backgroundColor: "#fff5e8",
                    borderRadius: "8px",
                  }}
                />
              ))}

              {remainingCount > 0 && (
                <Chip
                  label={`+${remainingCount} more`}
                  size="small"
                  sx={{
                    backgroundColor: "#fff5e8",
                    borderRadius: "8px",
                  }}
                />
              )}
            </>
          )
        ) : (
          <Box sx={{ color: "var(--app-text-muted)", fontSize: "15px" }}>
            {placeholder}
          </Box>
        )}
      </Box>

      <Icon
        icon="eva:arrow-ios-downward-fill"
        width="20"
        height="20"
        style={{
          color: "var(--app-text-muted)",
          marginLeft: "auto",
        }}
      />
    </Box>
  );
};

// Shape the raw API center list into the hub/spoke tree used by this component
const mapCentres = (list) =>
  (list || []).map((hub) => ({
    id: hub.id,
    label: hub.name?.trim(),
    type: hub.centerType,
    spokes: (hub.spokeCenters || []).map((s) => ({
      id: s.id,
      label: s.name?.trim(),
      type: s.centerType,
    })),
  }));

const CentersTreeView = ({ handleSelectChange, user, setUser, type }) => {
  const [page, setPage] = useState(1);
  const limit = 50;

  const { centres, isLoading, pagination } = useGetCentres({
    page,
    limit,
  });

  const [treeData, setTreeData] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    if (!isLoading && centres) {
      const formatted = mapCentres(centres);

      setTreeData((prev) => {
        if (page === 1) return formatted;

        const existingIds = new Set(prev.map((item) => item.id));

        const newItems = formatted.filter((item) => !existingIds.has(item.id));

        return [...prev, ...newItems];
      });

      setLoadingMore(false);
    }
  }, [centres, isLoading, page]);

  // Debounce search input before hitting the API
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 300);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const isSearching = debouncedSearchTerm.length > 0;

  // Search server-side so centres beyond the loaded pages are still found
  const { centres: searchCentres, isLoading: searchLoading } = useGetCentres(
    { page: 1, limit: 100, searchText: debouncedSearchTerm },
    isSearching,
  );

  const searchTreeData = useMemo(
    () => (isSearching ? mapCentres(searchCentres) : []),
    [isSearching, searchCentres],
  );

  const displayedTreeData = isSearching ? searchTreeData : treeData;

  const handleScroll = (e) => {
    if (isSearching) return;

    const target = e.currentTarget;

    const isNearBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight < 50;

    const hasMore = page < (pagination?.totalPages || 1);

    if (isNearBottom && hasMore && !loadingMore && !isLoading) {
      setLoadingMore(true);
      setPage((prev) => prev + 1);
    }
  };

  const allIds = useMemo(() => {
    const ids = [];
    treeData.forEach((hub) => {
      ids.push(hub.id);
      hub.spokes.forEach((s) => ids.push(s.id));
    });
    return ids;
  }, [treeData]);

  // Centres already assigned to this member that haven't been paginated into
  // treeData yet (e.g. member is in the 200th centre while only 50 are loaded).
  // Fetch them directly by id so their labels/checked state show immediately.
  const missingSelectedIds = useMemo(
    () => (user?.centerIds || []).filter((id) => !allIds.includes(id)),
    [user?.centerIds, allIds],
  );

  const { centres: missingCentres } = useGetCentres(
    {
      centerIds: JSON.stringify(missingSelectedIds),
      limit: missingSelectedIds.length,
    },
    missingSelectedIds.length > 0,
  );

  const idToLabel = useMemo(() => {
    const map = {};

    [...mapCentres(missingCentres), ...treeData].forEach((hub) => {
      map[hub.id] = hub.label;
      hub.spokes.forEach((s) => {
        map[s.id] = s.label;
      });
    });

    return map;
  }, [missingCentres, treeData]);

  const isAllSelected =
    allIds.length > 0 && allIds.every((id) => user?.centerIds?.includes(id));

  const handleToggle = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen((prev) => !prev);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm("");
    setDebouncedSearchTerm("");
  };

  const handleCheckboxChange = (id) => {
    let newSelected;

    if (user?.centerIds?.includes(id)) {
      newSelected = user?.centerIds?.filter((cid) => cid !== id);
    } else {
      newSelected = [...user?.centerIds, id];
    }

    setUser({ ...user, centerIds: newSelected });

    handleSelectChange?.(newSelected, "hub+spoke");
  };

  const handleAllCentersChange = () => {
    let newSelected = [];

    if (!isAllSelected) {
      newSelected = allIds;
    }

    setUser({ ...user, centerIds: newSelected });

    handleSelectChange?.(newSelected, "hub+spoke");
  };

  const selectedLabels = useMemo(() => {
    if (isAllSelected) return ["All Centers"];

    const labels = new Set();

    (user?.centerIds || []).forEach((id) => {
      if (idToLabel[id]) labels.add(idToLabel[id]);
    });

    return Array.from(labels);
  }, [idToLabel, user?.centerIds, isAllSelected]);

  const handleChipDelete = (label) => {
    if (label === "All Centers") {
      setUser({ ...user, centerIds: [] });
      handleSelectChange?.([], "hub+spoke");
      return;
    }

    const idToRemove = Object.keys(idToLabel).find(
      (id) => idToLabel[id] === label,
    );

    if (idToRemove) {
      const newSelected = user?.centerIds?.filter((id) => id !== idToRemove);

      setUser({ ...user, centerIds: newSelected });
      handleSelectChange?.(newSelected, "hub+spoke");
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <ControlBox
        selectedLabels={selectedLabels}
        placeholder="Enter centres you wish to include the member in"
        onClick={handleToggle}
        disabled={type === "view" || user?.role === "admin"}
        onDelete={handleChipDelete}
      />

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{ width: anchorEl?.offsetWidth }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            onScroll={handleScroll}
            sx={{
              maxHeight: 350,
              overflow: "auto",
              mt: 1,
              p: 1,
            }}
          >
            {/* Search Field */}
            <Box sx={{ px: 1, pb: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search centres..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  "& .MuiInputBase-root": {
                    borderRadius: "12px",

                    "& fieldset": {
                      borderColor: "var(--app-border)",
                    },

                    "&:hover fieldset": {
                      borderColor: "var(--app-border)",
                    },

                    "&.Mui-focused fieldset": {
                      borderColor: "var(--app-border)",
                      borderWidth: "1px",
                    },

                    "&.Mui-focused": {
                      boxShadow: "none",
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "var(--app-text-muted)" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* All Centers */}
            <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
              <Checkbox
                size="small"
                checked={isAllSelected}
                indeterminate={user?.centerIds?.length > 0 && !isAllSelected}
                onChange={handleAllCentersChange}
                disabled={type === "view" || user?.role === "admin"}
              />
              <Typography
                sx={{
                  fontWeight: "700",
                  color: "var(--app-text)",
                  fontSize: 15,
                }}
              >
                All Centers
              </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            {isSearching && searchLoading && (
              <Box textAlign="center" py={1}>
                <Typography fontSize={12}>Searching centres...</Typography>
              </Box>
            )}

            {isSearching && !searchLoading && displayedTreeData.length === 0 && (
              <Box textAlign="center" py={1}>
                <Typography fontSize={12} sx={{ color: "var(--app-text-muted)" }}>
                  No centres found
                </Typography>
              </Box>
            )}

            {displayedTreeData.map((hub, hubIdx) => (
              <Box key={hub.id}>
                <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
                  <Checkbox
                    size="small"
                    checked={user?.centerIds?.includes(hub.id)}
                    onChange={() => handleCheckboxChange(hub.id)}
                  />
                  <TypeIcon type={hub.type} size={15} />
                  <Typography sx={{ fontWeight: "700", fontSize: 15 }}>
                    {hub.label}
                  </Typography>
                </Box>

                {hub.spokes.map((spoke) => (
                  <Box
                    key={spoke.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      pl: 5,
                      py: 0.5,
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={user?.centerIds?.includes(spoke.id)}
                      onChange={() => handleCheckboxChange(spoke.id)}
                    />
                    <TypeIcon type={spoke.type} size={15} />
                    <Typography sx={{ fontSize: 15 }}>{spoke.label}</Typography>
                  </Box>
                ))}

                {hubIdx < displayedTreeData.length - 1 && (
                  <Divider sx={{ my: 1 }} />
                )}
              </Box>
            ))}

            {!isSearching && loadingMore && (
              <Box textAlign="center" py={1}>
                <Typography fontSize={12}>Loading more centres...</Typography>
              </Box>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default CentersTreeView;
